import React, { useEffect, useState } from "react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

const RATE_LABELS = { whatsapp_message: "WhatsApp message", sms: "SMS", qr_ticket: "QR ticket generation", marketing_campaign: "Marketing campaign" };

export default function AdminFinance() {
  const [bookings, setBookings] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // {kind, item}
  const [reference, setReference] = useState("");

  const load = () => Promise.all([
    api.get("/admin/bookings"), api.get("/admin/wallet-refunds"),
    api.get("/admin/settlements"), api.get("/admin/config"),
  ]).then(([b, r, s, c]) => {
    setBookings(b.data.bookings); setRefunds(r.data.requests); setSettlements(s.data.settlements);
    setRates(c.data.service_pricing || {});
  }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const saveRates = async () => {
    try { await api.put("/admin/config", { service_pricing: rates }); toast.success("Service rates updated. Organizers see them instantly."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const completeRefund = async () => {
    try { await api.post(`/admin/wallet-refunds/${dlg.item.id}/complete`, { reference }); toast.success("Refund completed."); setDlg(null); setReference(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const rejectRefund = async (id) => { try { await api.post(`/admin/wallet-refunds/${id}/reject`, {}); toast.success("Rejected."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const processSettle = async (id) => { try { await api.post(`/admin/settlements/${id}/process`); toast.success("Marked processing."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const rejectSettle = async (id) => { try { await api.post(`/admin/settlements/${id}/reject`, {}); toast.success("Settlement rejected."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const completeSettle = async () => {
    if (!reference.trim()) { toast.error("Enter a payment reference."); return; }
    try { await api.post(`/admin/settlements/${dlg.item.id}/complete`, { reference }); toast.success("Settlement completed."); setDlg(null); setReference(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <PageLoader />;

  return (
    <div>
      <div className="overline mb-1">Money</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Finance</h1>
      <Tabs defaultValue="rates">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="rates" data-testid="admin-tab-rates">Service Rates</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="admin-tab-settlements">Settlements ({settlements.filter((s) => s.status !== "completed").length})</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="admin-tab-refunds">Wallet Refunds ({refunds.filter((r) => r.status === "requested").length})</TabsTrigger>
          <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="rates" className="max-w-lg space-y-4">
          <p className="text-sm text-muted-foreground">Set per-action rates charged from the Organizer Services Wallet. Changes apply immediately.</p>
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {Object.keys(rates).map((k) => (
              <div key={k} className="flex items-center justify-between p-4">
                <span className="text-sm font-medium">{RATE_LABELS[k] || k}</span>
                <div className="relative w-32"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                  <Input type="number" step="0.01" value={rates[k]} onChange={(e) => setRates({ ...rates, [k]: Number(e.target.value) })} className="pl-7 h-10" data-testid={`rate-${k}`} /></div>
              </div>
            ))}
          </div>
          <Button onClick={saveRates} className="rounded-full" data-testid="save-rates">Save rates</Button>
        </TabsContent>

        <TabsContent value="settlements">
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {settlements.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-4 gap-3" data-testid={`admin-settlement-${s.id}`}>
                <div className="min-w-0"><div className="font-medium">{money(s.amount)} · {s.user_name || s.user_email}</div>
                  <div className="text-xs text-muted-foreground truncate">{new Date(s.created_at).toLocaleDateString("en-IN")} · A/C {s.payout_masked || "—"} {s.payment_details ? `· ${s.payment_details}` : ""}</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={s.status} />
                  {s.status === "requested" && <Button size="sm" variant="outline" onClick={() => processSettle(s.id)} data-testid={`process-${s.id}`}>Process</Button>}
                  {s.status !== "completed" && s.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => rejectSettle(s.id)} data-testid={`reject-settle-${s.id}`}>Reject</Button>}
                  {s.status !== "completed" && s.status !== "rejected" && <Button size="sm" onClick={() => { setDlg({ kind: "settle", item: s }); setReference(""); }} data-testid={`complete-settle-${s.id}`}>Mark paid</Button>}
                </div>
              </div>
            ))}
            {settlements.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No settlement requests.</div>}
          </div>
        </TabsContent>

        <TabsContent value="refunds">
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {refunds.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4 gap-3" data-testid={`admin-refund-${r.id}`}>
                <div className="min-w-0"><div className="font-medium">{money(r.amount)} · {r.user_name || r.user_email}</div>
                  <div className="text-xs text-muted-foreground truncate">{new Date(r.created_at).toLocaleDateString("en-IN")} · {r.payment_details}</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={r.status} />
                  {r.status === "requested" && <>
                    <Button size="sm" variant="outline" onClick={() => rejectRefund(r.id)} data-testid={`reject-refund-${r.id}`}>Reject</Button>
                    <Button size="sm" onClick={() => { setDlg({ kind: "refund", item: r }); setReference(""); }} data-testid={`complete-refund-${r.id}`}>Mark paid</Button>
                  </>}
                </div>
              </div>
            ))}
            {refunds.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No wallet refund requests.</div>}
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {bookings.map((b) => (
              <div key={b.id} className="flex items-center justify-between p-4">
                <div><div className="font-medium">{b.event_title}</div><div className="text-xs text-muted-foreground">{b.invoice_number} · {new Date(b.created_at).toLocaleDateString("en-IN")}</div></div>
                <div className="flex items-center gap-3"><span className="font-semibold">{money(b.total)}</span><StatusBadge status={b.status} /></div>
              </div>
            ))}
            {bookings.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No bookings yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm transfer & mark completed</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Amount: <b>{dlg && money(dlg.item.amount)}</b>. Enter the bank/UPI payment reference after transferring manually.</p>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Payment reference / UTR" className="mt-3" data-testid="transfer-reference" />
          <DialogFooter>
            <Button onClick={dlg?.kind === "refund" ? completeRefund : completeSettle} className="w-full rounded-full" data-testid="confirm-transfer">Mark completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
